// src/components/ColumnSelectorDialog.js
import React, { useState, useMemo, useEffect, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  IconButton,
  TextField,
  Chip,
  Stack,
  Paper,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Tooltip as MuiTooltip,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, Delete as DeleteIcon, Search as SearchIcon, Edit as EditIcon, LinearScale as ScaleIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';

/**
 * ColumnSelectorDialog
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - project: { projectID, rawData: Array<ColumnMeta> }
 *  - onSave: (projectID, newRawDataArray) => Promise<void> | void
 */
const ColumnSelectorDialog = forwardRef(function ColumnSelectorDialog({ open, onClose, project, onSave }, ref) {
  const { t } = useTranslation();

  const [groups, setGroups] = useState([]);
  const [ungrouped, setUngrouped] = useState([]);
  const [active, setActive] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('');
  const [lastIndex, setLastIndex] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [targetGroup, setTargetGroup] = useState('');
  const GROUP_TYPES = ['multiplechoice', 'griddata', 'multigriddata'];

  const [showLabels, setShowLabels] = useState(false);
  const [saving, setSaving] = useState(false);

  // auto-group control
  const [resetTick, setResetTick] = useState(0);     // Reset'e basıldıkça artar
  const [didInitialBuild, setDidInitialBuild] = useState(false); // İlk açılışta bir kez uygula

  // inline label editing state
  const [labelEdits, setLabelEdits] = useState(new Map());
  const [labelDlgOpen, setLabelDlgOpen] = useState(false);
  const [labelDlgHdr, setLabelDlgHdr] = useState('');
  const [labelDlgVarLabel, setLabelDlgVarLabel] = useState('');
  const [labelDlgValueLabels, setLabelDlgValueLabels] = useState(''); // "code=label" lines

  // header -> column meta map
  const headerMetaMap = useMemo(() => {
    const m = new Map();
    (project?.rawData || []).forEach((c) => m.set(c.header, c));
    return m;
  }, [project]);

  // LIKERT state (per column & group)
  const [likertByHeader, setLikertByHeader] = useState(new Map()); // hdr -> { isLikert, scale:[{code,label,value}] }
  const [likertByGroup, setLikertByGroup] = useState(new Map());   // gid -> { isLikert, scale:[...] }

  const [likertDlgOpen, setLikertDlgOpen] = useState(false);
  const [likertDlgTarget, setLikertDlgTarget] = useState({ kind: '', id: '' }); // 'header' | 'group'
  const [likertDlgIsLikert, setLikertDlgIsLikert] = useState(false);
  const [likertDlgScale, setLikertDlgScale] = useState([]);

  // valueLabels > options öncelikli, 1..N sırala ve value=1..N ata
  const _extractDefaultScaleFromMeta = useCallback((meta) => {
    if (!meta) return [];
    const out = [];
    if (meta.valueLabels && typeof meta.valueLabels === 'object' && Object.keys(meta.valueLabels).length) {
      for (const [code, label] of Object.entries(meta.valueLabels)) {
        out.push({ code: String(code), label: String(label), value: 0 });
      }
      out.sort((a,b)=>{
        const na = parseFloat(a.code), nb = parseFloat(b.code);
        if (!Number.isNaN(na) && !Number.isNaN(nb)) return na-nb;
        return String(a.code).localeCompare(String(b.code));
      });
    } else if (Array.isArray(meta.options) && meta.options.length) {
      for (const opt of meta.options) {
        const s = String(opt);
        const m = s.match(/^\\s*(\\d+)\\s*[-–:]?\\s*(.*)$/); // "5 - Çok iyi"
        const code = m ? m[1] : s;
        const label = m ? (m[2] || s).trim() : s;
        out.push({ code: String(code), label, value: 0 });
      }
      out.sort((a,b)=>{
        const na = parseFloat(a.code), nb = parseFloat(b.code);
        if (!Number.isNaN(na) && !Number.isNaN(nb)) return na-nb;
        return String(a.code).localeCompare(String(b.code));
      });
    }
    out.forEach((r, i) => r.value = i + 1);
    return out;
  }, []);

  const openLikertEditorForHeader = useCallback((hdr) => {
    const current = likertByHeader.get(hdr) || { isLikert:false, scale:[] };
    const meta = headerMetaMap.get(hdr);
    const scale = current.scale?.length ? current.scale : _extractDefaultScaleFromMeta(meta);
    setLikertDlgTarget({ kind:'header', id:hdr });
    setLikertDlgIsLikert(!!current.isLikert);
    setLikertDlgScale(scale);
    setLikertDlgOpen(true);
  }, [likertByHeader, headerMetaMap, _extractDefaultScaleFromMeta]);

  const openLikertEditorForGroup = useCallback((gid) => {
    const current = likertByGroup.get(gid) || { isLikert:false, scale:[] };
    const g = (groups||[]).find(x => x.id === gid);
    const firstHdr = g?.columns?.[0];
    const meta = firstHdr ? headerMetaMap.get(firstHdr) : null;
    const scale = current.scale?.length ? current.scale : _extractDefaultScaleFromMeta(meta);
    setLikertDlgTarget({ kind:'group', id:gid });
    setLikertDlgIsLikert(!!current.isLikert);
    setLikertDlgScale(scale);
    setLikertDlgOpen(true);
  }, [likertByGroup, groups, headerMetaMap, _extractDefaultScaleFromMeta]);

  // OTHER triplet linking (base -> otherText/otherCode)
  const [otherLinks, setOtherLinks] = useState(new Map()); // key: base header (e.g., 'F4'), val: { text: 'F4_4_other', code: 'F4_4_other_code1' }
  const [otherDlgOpen, setOtherDlgOpen] = useState(false);
  const [newOtherBase, setNewOtherBase] = useState('');
  const [newOtherText, setNewOtherText] = useState('');
  const [newOtherCode, setNewOtherCode] = useState('');
  
  // Persist Other links after Reset
  const [persistOtherAfterReset, setPersistOtherAfterReset] = useState(false);
  const [otherSaving, setOtherSaving] = useState(false);
  const pendingPersistAfterResetRef = useRef(false);
  const triggerReset = useCallback(() => {
    setResetTick((v) => v + 1);
    setPersistOtherAfterReset(true);
  }, []);
  const handleReset = useCallback(() => {
    pendingPersistAfterResetRef.current = true;
    triggerReset();
  }, [triggerReset]);

  useImperativeHandle(
    ref,
    () => ({
      reset: handleReset,
    }),
    [handleReset]
  );

  const surfaceBaseSx = {
    px: { xs: 2, md: 3 },
    py: { xs: 1.75, md: 2.25 },
  };

  const primaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    px: { xs: 2, md: 2.5 },
    py: 0.9,
    borderRadius: 999,
    boxShadow: '0 12px 24px rgba(148, 163, 184, 0.24)',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    '&:hover': {
      boxShadow: '0 16px 28px rgba(148, 163, 184, 0.3)',
    },
  };

  const chipSx = {
    borderColor: 'rgba(148, 163, 184, 0.4)',
    bgcolor: 'rgba(224, 231, 255, 0.6)',
    color: '#1f2937',
    fontWeight: 600,
    '& .MuiChip-icon': { color: '#3b82f6' },
  };

  const heroChips = [
    project?.projectID ? `Proje: ${project.projectID}` : null,
    `Gruplar: ${groups.length}`,
    `Serbest: ${ungrouped.length}`,
    `Aktif: ${active.size}`,
  ].filter(Boolean);
  // Helper to persist only Other links silently (does not close dialog)
  const persistOtherLinksSilently = useCallback(async (linkMap) => {
    if (!project || typeof onSave !== 'function') return;
    const map = {};
    (project.rawData || []).forEach((c) => (map[c.header] = { ...c }));

    // 1) Collect previously linked other headers to re-activate them
    const previouslyLinked = new Set();
    (project.rawData || []).forEach((col) => {
      if (col.otherTextHeader) previouslyLinked.add(col.otherTextHeader);
      if (col.otherCodeHeader) previouslyLinked.add(col.otherCodeHeader);
    });

    // 2) Clear ALL existing other links on base columns
    Object.values(map).forEach((c) => {
      if ('otherTextHeader' in c) delete c.otherTextHeader;
      if ('otherCodeHeader' in c) delete c.otherCodeHeader;
    });

    // 3) Re-activate any columns that were previously hidden due to old links
    previouslyLinked.forEach((h) => {
      if (map[h]) map[h].active = true;
    });

    // 4) Apply NEW links from linkMap and hide only those referenced now
    linkMap.forEach((v, base) => {
      const codes = Array.isArray(v.codes) ? v.codes.filter(Boolean) : (v.code ? [v.code] : []);
      if (map[base]) {
        map[base].otherTextHeader = v.text || '';
        map[base].otherCodeHeader   = codes[0] || ''; // uyumluluk
        map[base].otherCodeHeaders  = codes;          // tam liste
      }
      if (v.text && map[v.text]) map[v.text].active = false;
      codes.forEach(c => { if (c && map[c]) map[c].active = false; });
    });


    // Upsert sys_othersDataset meta column (project-level persisted dataset)
    const othersArray = [];
    linkMap.forEach((v, base) => {
      const codes = Array.isArray(v.codes) ? v.codes : (v.code ? [v.code] : []);
      othersArray.push({ base, text: v.text || '', codes });
    });
    const metaKey = 'sys_othersDataset';
    const existingMeta = map[metaKey];
    const metaPayload = {
      header: metaKey,
      type: 'meta',
      active: false,
      varLabel: JSON.stringify(othersArray), // JSON payload; okuyucu buradan da parse eder
      othersDataset: othersArray,
    };
    map[metaKey] = existingMeta ? { ...existingMeta, ...metaPayload } : metaPayload;

    try {
      await onSave(project.projectID, Object.values(map));
    } catch (e) {
      console.error('persistOtherLinksSilently error', e);
    }
  }, [project, onSave]);


  // Helpers to detect candidates
  const ALL_HEADERS = useMemo(() => (project?.rawData || []).map(c => c.header), [project]);

  const isOtherText = useCallback((h) => /_other$/i.test(h), []);
  const isOtherCode = useCallback((h) => /_other_?(?:code|kod)[0-9a-zA-Z]+$/i.test(h), []);
  const isPureBase = useCallback((h) => {
    if (!h) return false;
    // Only exclude explicit other-text/other-code columns; allow grid suffixes as selectable bases too
    return !(isOtherText(h) || isOtherCode(h));
  }, [isOtherText, isOtherCode]);
  const baseRootOf = useCallback((h) => String(h || '').split('_')[0], []);

  // Helper: Extract family key before _other (e.g., 'S8c_12_other' -> 'S8c_12')
  const familyOfOther = useCallback((h) => {
    const m = String(h || '').match(/^(.+?)_other(?:_(?:code|kod)[0-9a-zA-Z]+)?$/i);
    return m ? m[1] : null;
  }, []);

  const autoBuildOtherLinks = useCallback(() => {
    const textByFamily = new Map(); // 'S8c_12' -> 'S8c_12_other'
    const codeByFamily = new Map(); // 'S8c_12' -> 'S8c_12_other_code1'
    const textByRoot   = new Map(); // 'F4' -> 'F4_4_other' (fallback for single-base only)
    const codeByRoot   = new Map();

    // Track base counts per root and whether that root has any family-specific others
    const rootBaseCount = new Map(); // 'S8c' -> N
    const rootHasFamilyOther = new Map(); // 'S8c' -> true if any 'S8c_*_other' exists

    // Pre-scan bases to count by root
    const PURE_BASES = ALL_HEADERS.filter(isPureBase);
    for (const b of PURE_BASES) {
      const r = baseRootOf(b);
      rootBaseCount.set(r, (rootBaseCount.get(r) || 0) + 1);
    }

    // Index other text/code
    for (const h of ALL_HEADERS) {
      if (isOtherText(h)) {
        const fam = familyOfOther(h);
        if (fam) {
          textByFamily.set(fam, h);
          rootHasFamilyOther.set(baseRootOf(fam), true);
        }
        const root = baseRootOf(h);
        if (root && !textByRoot.has(root)) textByRoot.set(root, h);
      } else if (isOtherCode(h)) {
        const fam = familyOfOther(h);
        if (fam) {
          const arr = codeByFamily.get(fam) || [];
          arr.push(h); codeByFamily.set(fam, arr);
          rootHasFamilyOther.set(baseRootOf(fam), true);
        }
        const root = baseRootOf(h);
        if (root) { const arr = codeByRoot.get(root) || []; arr.push(h); codeByRoot.set(root, arr); }
      }
    }

    const map = new Map();
    for (const base of PURE_BASES) {
      const root = baseRootOf(base);
      const familyKey = base; // e.g., 'S8c_12'

      // Prefer exact family match
      const tFam = textByFamily.get(familyKey);
      const cFamArr = codeByFamily.get(familyKey);
      if (tFam && Array.isArray(cFamArr) && cFamArr.length) {
        map.set(base, { text: tFam, codes: cFamArr }); continue;
      }

      // Decide if root fallback is allowed
      // Rule: Allow only when this root has exactly one base (e.g., F4). This prevents S8c_* from stealing S8c_12_other.
      const allowRootFallback = (rootBaseCount.get(root) === 1);
      if (allowRootFallback) {
        const tRoot = textByRoot.get(root);
        const cRootArr = codeByRoot.get(root);
        if (tRoot && Array.isArray(cRootArr) && cRootArr.length) {
          map.set(base, { text: tRoot, codes: cRootArr });
        }
      }
      // else: do nothing (no match) — prevents S8c_1.. from stealing S8c_12_other
    }
    return map;
  }, [ALL_HEADERS, isOtherText, isOtherCode, isPureBase, baseRootOf, familyOfOther]);

  const isHiddenByOther = useCallback((h) => {
    for (const [, v] of otherLinks) {
      if (v.text === h) return true;
      const codes = Array.isArray(v.codes) ? v.codes : (v.code ? [v.code] : []);
      if (codes.includes(h)) return true;
    }
    return false;
  }, [otherLinks]);

  const getEffectiveVarLabel = useCallback((hdr) => {
    const edited = labelEdits.get(hdr);
    if (edited && typeof edited.varLabel === 'string' && edited.varLabel.trim() !== '') {
      return edited.varLabel.trim();
    }
    const meta = headerMetaMap.get(hdr);
    return meta?.varLabel || '';
  }, [labelEdits, headerMetaMap]);

  const getEffectiveValueLabels = useCallback((hdr) => {
    const edited = labelEdits.get(hdr);
    if (edited && edited.valueLabels && typeof edited.valueLabels === 'object') {
      return edited.valueLabels;
    }
    const meta = headerMetaMap.get(hdr);
    return (meta?.valueLabels && typeof meta.valueLabels === 'object') ? meta.valueLabels : {};
  }, [labelEdits, headerMetaMap]);

  const getDisplayName = useCallback(
    (hdr) => {
      const vlabel = getEffectiveVarLabel(hdr);
      if (showLabels && vlabel) {
        return `${vlabel} (${hdr})`;
      }
      return hdr;
    },
    [getEffectiveVarLabel, showLabels]
  );

  const getValueLabelPreview = useCallback(
    (hdr) => {
      const vl = getEffectiveValueLabels(hdr);
      if (!vl || typeof vl !== 'object') return '';
      const entries = Object.entries(vl).slice(0, 5).map(([k, v]) => `${k}: ${v}`);
      const more = Object.keys(vl).length > 5 ? '…' : '';
      return entries.length ? `Değer etiketleri: ${entries.join(' • ')}${more}` : '';
    },
    [getEffectiveValueLabels]
  );

  /* -------- DnD handler -------- */
  const handleDragEnd = ({ source, destination, draggableId }) => {
    if (!destination) return;

    // same list reorder
    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === 'ungrouped') {
        setUngrouped((u) => {
          const copy = [...u];
          copy.splice(source.index, 1);
          copy.splice(destination.index, 0, draggableId);
          return copy;
        });
      } else {
        const gid = source.droppableId.replace('group-', '');
        setGroups((gs) =>
          gs.map((g) => {
            if (g.id !== gid) return g;
            const cols = [...g.columns];
            cols.splice(source.index, 1);
            cols.splice(destination.index, 0, draggableId);
            return { ...g, columns: cols };
          }),
        );
      }
      return;
    }

    // move between lists
    if (source.droppableId === 'ungrouped') {
      setUngrouped((u) => u.filter((h) => h !== draggableId));
    } else {
      const srcG = source.droppableId.replace('group-', '');
      setGroups((gs) =>
        gs.map((g) =>
          g.id === srcG ? { ...g, columns: g.columns.filter((h) => h !== draggableId) } : g,
        ),
      );
    }

    if (destination.droppableId === 'ungrouped') {
      setUngrouped((u) => {
        const copy = [...u];
        copy.splice(destination.index, 0, draggableId);
        return copy;
      });
      setActive((a) => {
        const n = new Set(a);
        n.delete(draggableId);
        return n;
      });
    } else {
      const dstG = destination.droppableId.replace('group-', '');
      setGroups((gs) =>
        gs.map((g) => {
          if (g.id !== dstG) return g;
          const cols = [...g.columns];
          cols.splice(destination.index, 0, draggableId);
          return { ...g, columns: cols };
        }),
      );
      setActive((a) => {
        const n = new Set(a);
        n.delete(draggableId);
        return n;
      });
    }
  };

  // Pattern-aware auto-grouping base extractor (deterministic)
  const extractAutoGroupBase = useCallback((hdr) => {
    if (!hdr || typeof hdr !== 'string') return null;
    const s = hdr.trim();

    // (-1) Grid/row patterns
    //    <base>_r<row>_c<col>  => base: <base>
    //    <base>_r<row>         => base: <base>
    const mGridRowCol = s.match(/^(.+?)_r\d+_c\d+$/i);
    if (mGridRowCol) {
      const base = mGridRowCol[1].replace(/[\s_\-]+$/g, '');
      if (/^[A-Za-z]$/.test(base)) return null;
      return base;
    }
    const mRowOnly = s.match(/^(.+?)_r\d+$/i);
    if (mRowOnly) {
      const base = mRowOnly[1].replace(/[\s_\-]+$/g, '');
      if (/^[A-Za-z]$/.test(base)) return null;
      return base;
    }

    // 0a) <prefix>_<digit+>_other(_codeX)?  -> base: <prefix>
    //     Ör: S13b_12_other, S13b_12_other_code1  => S13b
    const mOtherCode = s.match(/^([^_]+)_\d+_other(?:_code[0-9a-zA-Z]+)?$/i);
    if (mOtherCode) {
      let base = mOtherCode[1].replace(/[\s_\-]+$/g, '');
      if (/^[A-Za-z]$/.test(base)) return null;
      if (!/\d/.test(base)) return null;
      return base;
    }

    // 0) <prefix>_<digit+>_<token>  -> base: <prefix>
    //    Ör: S4_1_w, S4_2_b, S15a_3_w  => S4, S15a
    //    Not: prefix tek harfse (F, S) ele (over-grouping engeli)
    const mTriplet = s.match(/^([^_]+)_(\d+)_([A-Za-z0-9]+)$/);
    if (mTriplet) {
      let base = mTriplet[1].replace(/[\s_\-]+$/g, '');
      if (/^[A-Za-z]$/.test(base)) return null;
      // Sayısal karakter içermeyen çok kısa prefixleri de ele
      if (!/\d/.test(base)) return null;
      return base;
    }

    // 1) <prefix>_cevap<digit+>([_]code<digit+>)?  -> base: <prefix>_cevap
    //    Ör: S15a_CEVAP1, S15a_CEVAP10_code1  => S15a_cevap
    const mCevap = s.match(/^(.*?_)(?:cevap)(\d+)(?:[_]?code[0-9a-zA-Z]+)?$/i);
    if (mCevap) {
      let base = mCevap[1]; // includes trailing underscore
      base = base.replace(/[\s_\-]+$/g, '');
      base = `${base}_cevap`;
      // Tek harfli base'i engelle
      if (/^[A-Za-z]$/.test(base)) return null;
      return base;
    }

    // 2) <prefix><digit+>_code<digit+> or _kod<digit+> -> base: <prefix>_code<digit+> or _kod<digit+>
    //    Ör: S16A1_code2 => S16A_code2, S16A1_kod2 => S16A_kod2
    const mCodeAfterNum = s.match(/^(.*?)(\d+)[_]?((?:code|kod))([0-9a-zA-Z]+)$/i);
    if (mCodeAfterNum) {
      let base = `${mCodeAfterNum[1]}_${mCodeAfterNum[3]}${mCodeAfterNum[4]}`;
      base = base.replace(/[\s_\-]+$/g, '').replace(/_+/g, '_');
      if (/^[A-Za-z]$/.test(base)) return null;
      return base;
    }

    // 3) <prefix><digit+>$ -> base: <prefix> (ör: S16a01 => S16a)
    const mPlainNum = s.match(/^(.*?)(\d+)$/);
    if (mPlainNum) {
      let base = mPlainNum[1].replace(/[\s_\-]+$/g, '');
      // base içinde rakam varsa veya '_' içeriyorsa izin ver
      const hasDigit = /\d/.test(base);
      if (/^[A-Za-z]$/.test(base)) return null;
      if (!hasDigit && !/_/.test(base)) return null;
      return base;
    }

    return null;
  }, []);

  // Classify a single header by underscore + r/c pattern
  const classifyHeaderPattern = useCallback((hdr) => {
    if (typeof hdr !== 'string') return null;
    // Two-underscore grid with both row & column => multigriddata (e.g., M12_r1_c1 or M12_c1_r1)
    if (/(?:_r\d+_c\d+|_c\d+_r\d+)$/i.test(hdr)) return 'multigriddata';
    // Exactly one underscore and suffix begins with r or c + digits => griddata (e.g., S9_c1, R0_r1)
    if (/^[^_]+_(?:r|c)\d+$/i.test(hdr)) return 'griddata';
    return null; // anything else falls back to multiplechoice at group level
  }, []);

  // Decide group type from its headers using the new rules
  const resolveGroupTypeFromHeaders = useCallback((headers) => {
    if (!Array.isArray(headers)) return 'multiplechoice';
    let hasMultiGrid = false;
    let hasGrid = false;
    for (const h of headers) {
      const t = classifyHeaderPattern(h);
      if (t === 'multigriddata') { hasMultiGrid = true; break; }
      if (t === 'griddata') hasGrid = true;
    }
    if (hasMultiGrid) return 'multigriddata';
    if (hasGrid) return 'griddata';
    return 'multiplechoice';
  }, [classifyHeaderPattern]);

  /* -------- build lists from incoming project -------- */
  useEffect(() => {
    if (!project) return; // proje yoksa yükleme

    const rows = (project.rawData || []).filter(col => col.type !== 'empty');

    // Load persisted Other links from a hidden meta column if present
    let otherMap = new Map();
    const sysOthersCol = rows.find(c => c && c.header === 'sys_othersDataset');
    if (sysOthersCol && (sysOthersCol.othersDataset || sysOthersCol.varLabel)) {
      try {
        const persisted = Array.isArray(sysOthersCol.othersDataset)
          ? sysOthersCol.othersDataset
          : (typeof sysOthersCol.varLabel === 'string' && sysOthersCol.varLabel.trim().match(/^[[{]/)
              ? JSON.parse(sysOthersCol.varLabel)
              : null);
        if (Array.isArray(persisted)) {
          persisted.forEach(item => {
            const base  = item?.base;
            if (!base) return;
            const text  = item.text || '';
            const codes = Array.isArray(item.codes) ? item.codes.filter(Boolean) : (item.code ? [item.code] : []);
            otherMap.set(base, { text, codes });
          });
        }
      } catch (e) { console.warn('othersDataset parse error', e); }
    }
    
    // If user has never configured anything (no groupname/manualUngroup/visibility/label),
    // treat the first open exactly like a Reset. Otherwise, respect existing config.
    const hasAnyExplicitConfig = rows.some((c) =>
      (c.groupname && String(c.groupname).trim() !== '') ||
      c.manualUngroup === true ||
      typeof c.groupVisible === 'boolean' ||
      (typeof c.groupLabel === 'string' && c.groupLabel.trim() !== '')
    );
    const shouldAutoGroup = resetTick > 0 || (!didInitialBuild && !hasAnyExplicitConfig);

    const gm = {};
    let un = [];
    const act = new Set();

    // seed label edits from incoming project
    const lblMap = new Map();
    rows.forEach((col) => {
      lblMap.set(col.header, {
        varLabel: (typeof col.varLabel === 'string' ? col.varLabel : ''),
        valueLabels: (col.valueLabels && typeof col.valueLabels === 'object') ? col.valueLabels : {},
      });
    });

    // seed from existing project rawData (persisted choices)

    (project.rawData || []).forEach(col => {
      if (col.otherTextHeader || col.otherCodeHeader) {
        if (col.header && !otherMap.has(col.header)) {
          otherMap.set(col.header, { text: col.otherTextHeader || '', code: col.otherCodeHeader || '' });
        }
      }
    });

    // Fallback: only on initial auto-setup or explicit Reset; do NOT resurrect links after manual removals
    if (otherMap.size === 0 && shouldAutoGroup) {
      const fallback = autoBuildOtherLinks();
      if (fallback && fallback.size) {
        otherMap = fallback;
      }
    }

    if (shouldAutoGroup) {
      // SIFIRDAN kuralları uygula: var olan groupname'leri yok say
      rows.forEach(col => act.add(col.header));
      un = rows.map(c => c.header);

      // Auto-detect Other triplets and merge into otherMap (union codes if base already exists)
      const autoMap = autoBuildOtherLinks();
      autoMap.forEach((v, k) => {
        const exist = otherMap.get(k);
        if (!exist) {
          otherMap.set(k, v);
          return;
        }
        // Merge: keep text (prefer existing), and UNION code arrays
        const text  = exist.text || v.text || '';
        const exArr = Array.isArray(exist.codes) ? exist.codes.slice() : (exist.code ? [exist.code] : []);
        const vArr  = Array.isArray(v.codes) ? v.codes : (v.code ? [v.code] : []);
        const merged = Array.from(new Set([...exArr, ...vArr].filter(Boolean)));
        otherMap.set(k, { text, codes: merged });
      });

      const autoBuckets = new Map();
      const addAuto = (hdr) => {
        // Do not auto-group purely from `_other` fields
        if (isOtherText(hdr) || isOtherCode(hdr)) return;
        const base = extractAutoGroupBase(hdr);
        if (!base) return;
        const key = base.toLowerCase();
        if (!autoBuckets.has(key)) autoBuckets.set(key, { base, headers: [] });
        autoBuckets.get(key).headers.push(hdr);
      };
      un.forEach(addAuto);

      autoBuckets.forEach(({ base, headers }) => {
        if (headers.length < 2) return; // tek elemanlı grup oluşturma
        if (!gm[base]) {
          const firstHdr = headers[0];
          const meta = headerMetaMap.get(firstHdr);
          const firstLabel = (meta && typeof meta.varLabel === 'string') ? meta.varLabel.trim() : '';
          gm[base] = {
            id: base,
            name: base,
            columns: [],
            // initial guess; finalized after collecting all headers
            grouptype: classifyHeaderPattern(firstHdr) || 'multiplechoice',
            visible: true,
            label: firstLabel || ''
          };
        }
        gm[base].columns.push(...headers);
      });

      // Ensure correct grouptype after collecting all headers using helper
      Object.values(gm).forEach((g) => {
        g.grouptype = resolveGroupTypeFromHeaders(g.columns || []);
      });

      const autoGroupedSet = new Set();
      Object.values(gm).forEach(g => g.columns.forEach(h => autoGroupedSet.add(h)));
      un = un.filter(h => !autoGroupedSet.has(h));
    } else {
      // SADECE mevcut explicit grupları uygula; otomatik gruplama yapma
      rows.forEach((col) => {
        if (col.active !== false) act.add(col.header);
        const meta = col.groupname ? col : {};
        if (['multiplechoice', 'griddata', 'multigriddata'].includes(meta.grouptype) && meta.groupname) {
          if (!gm[meta.groupname]) {
            gm[meta.groupname] = {
              id: meta.groupname,
              name: meta.groupname,
              columns: [],
              grouptype: meta.grouptype || 'multiplechoice',
              visible: (typeof col.groupVisible === 'boolean') ? col.groupVisible : true,
              label: (typeof col.groupLabel === 'string') ? col.groupLabel : '',
            };
          }
          gm[meta.groupname].columns.push(col.header);
          if (typeof col.groupVisible === 'boolean') gm[meta.groupname].visible = col.groupVisible;
          if (typeof col.groupLabel === 'string' && col.groupLabel.trim() !== '') gm[meta.groupname].label = col.groupLabel.trim();
        } else {
          un.push(col.header);
        }
      });
    }

    // 2.b) Otomatik gruba alınanları ungrouped listesinden çıkar
    const autoGroupedSet = new Set();
    Object.values(gm).forEach((g) => g.columns.forEach((h) => autoGroupedSet.add(h)));
    un = un.filter((h) => !autoGroupedSet.has(h));

    // Remove linked other headers from visible lists
    const linkedSet = new Set();
    otherMap.forEach(v => {
      if (v.text) linkedSet.add(v.text);
      (Array.isArray(v.codes) ? v.codes : (v.code ? [v.code] : []))
        .forEach(c => linkedSet.add(c));
    });
    // from ungrouped
    un = un.filter(h => !linkedSet.has(h));
    // from groups
    Object.values(gm).forEach(g => { if (Array.isArray(g.columns)) g.columns = g.columns.filter(h => !linkedSet.has(h)); });
    // Drop groups that ended up empty after removing linked others
    Object.keys(gm).forEach(key => {
      const g = gm[key];
      if (!g || !Array.isArray(g.columns) || g.columns.length === 0) {
        delete gm[key];
      }
    });

    // 2.d) Force any unlinked _other or _other_code headers to be inactive (unchecked)
    un.forEach(h => {
      if (isOtherText(h) || isOtherCode(h)) {
        act.delete(h);
      }
    });

    // 2.c) sys_* ile başlayan kolonları varsayılan olarak pasif (unchecked) yap
    //     Bunu sadece ilk otomatik kurulumda veya Reset'te uygula
    if (shouldAutoGroup) {
      (project.rawData || []).forEach((col) => {
        if (/^sys_/i.test(col.header)) {
          act.delete(col.header);
        }
      });
    }

    setOtherLinks(otherMap);
    // Persist auto-generated Other links after Reset, silently (no dialog close)
    if (persistOtherAfterReset) {
      (async () => {
        await persistOtherLinksSilently(otherMap);
        setPersistOtherAfterReset(false);
      })();
    }
    setGroups(Object.values(gm));
    setUngrouped(un);
    setActive(act);
    setSelected(new Set());
    setTargetGroup('');
    setFilter('');
    setLabelEdits(lblMap);
    setDidInitialBuild(true);
   
    // Likert otomatik/restore
    const nextHdr = new Map(likertByHeader);
    const nextGrp = new Map(likertByGroup);

    // Persist edilmiş kolonlardan geri yükle
    (project.rawData || []).forEach(col => {
      const hdr = col.header;
      if (!hdr) return;
      if (col.isLikert || col.type === 'likert_scale') {
        if (!nextHdr.has(hdr)) {
          const meta = headerMetaMap.get(hdr);
          const scale = Array.isArray(col.likertScale) && col.likertScale.length
            ? col.likertScale
            : _extractDefaultScaleFromMeta(meta);
          nextHdr.set(hdr, { isLikert:true, scale });
        }
      }
    });

    // Gruplar için: herhangi bir üye likert_scale ise tüm grup likert
    Object.values(gm).forEach(g => {
      const anyLikert = (g.columns||[]).some(h => headerMetaMap.get(h)?.type === 'likert_scale');
      if (anyLikert) {
        if (!nextGrp.has(g.id)) {
          const likHdr = (g.columns||[]).find(h => headerMetaMap.get(h)?.type === 'likert_scale') || g.columns?.[0];
          const meta = likHdr ? headerMetaMap.get(likHdr) : null;
          nextGrp.set(g.id, { isLikert:true, scale:_extractDefaultScaleFromMeta(meta) });
        } else {
          const cur = nextGrp.get(g.id) || { isLikert:false, scale:[] };
          if (!cur.scale?.length) {
            const meta = headerMetaMap.get((g.columns||[])[0]);
            cur.scale = _extractDefaultScaleFromMeta(meta);
          }
          cur.isLikert = true;
          nextGrp.set(g.id, cur);
        }
      }
    });

    if (shouldAutoGroup) {
      setLikertByHeader(nextHdr);
      setLikertByGroup(nextGrp);
    } else {
      // manuel açılışta persisted groupLikert'i üyelerden toparla
      const restoredGrp = new Map();
      Object.values(gm).forEach(g => {
        const anyMemberMeta = (g.columns||[]).map(h => headerMetaMap.get(h)).find(m => m?.groupLikert);
        if (anyMemberMeta) {
          restoredGrp.set(g.id, {
            isLikert: true,
            scale: Array.isArray(anyMemberMeta.groupLikertScale) && anyMemberMeta.groupLikertScale.length
              ? anyMemberMeta.groupLikertScale
              : _extractDefaultScaleFromMeta(anyMemberMeta)
          });
        }
      });
      if (restoredGrp.size) setLikertByGroup(restoredGrp);
    }
  }, [project, resetTick, didInitialBuild, autoBuildOtherLinks, headerMetaMap, persistOtherAfterReset, persistOtherLinksSilently]);

  /* -------- delete group -------- */
  const handleDeleteGroup = (group) => {
    setGroups((gs) => gs.filter((g) => g.id !== group.id));
    setUngrouped((u) => [...u, ...group.columns]);
    setActive((a) => {
      const n = new Set(a);
      group.columns.forEach((h) => n.add(h));
      return n;
    });
  };

  /* -------- helpers -------- */
  const filteredUngrouped = useMemo(() => {
    if (!filter) return ungrouped.filter((h) => !isHiddenByOther(h));
    const q = filter.toLowerCase();
    return ungrouped.filter((h) => {
      if (isHiddenByOther(h)) return false;
      const meta = headerMetaMap.get(h);
      const label = meta?.varLabel ? String(meta.varLabel).toLowerCase() : '';
      return h.toLowerCase().includes(q) || label.includes(q);
    });
  }, [ungrouped, filter, headerMetaMap, isHiddenByOther]);

  const toggleActive = (hdr) =>
    setActive((a) => {
      const n = new Set(a);
      n.has(hdr) ? n.delete(hdr) : n.add(hdr);
      return n;
    });

  const toggleSelectByIdx = (idx, hdr, e) => {
    setSelected((sel) => {
      const n = new Set(sel);
      if (e.shiftKey && lastIndex !== null) {
        const [s, eI] = [lastIndex, idx].sort((a, b) => a - b);
        filteredUngrouped.slice(s, eI + 1).forEach((h) => n.add(h));
      } else {
        n.has(hdr) ? n.delete(hdr) : n.add(hdr);
      }
      return n;
    });
    setLastIndex(idx);
  };

  const addGroup = () => {
    const name = newGroupName.trim();
    if (!name || groups.find((g) => g.id === name)) return;
    setGroups((g) => [...g, { id: name, name, columns: [], grouptype: 'multiplechoice' }]);
    setNewGroupName('');
    setTargetGroup(name);
  };

  const setGroupType = (gid, newType) =>
    setGroups((gs) =>
      gs.map((g) => (g.id === gid ? { ...g, grouptype: newType } : g)),
    );

  const setGroupVisibility = (gid, vis) =>
    setGroups((gs) =>
      gs.map((g) => (g.id === gid ? { ...g, visible: !!vis } : g)),
    );

  const setGroupLabel = (gid, newLabel) =>
    setGroups((gs) =>
      gs.map((g) => (g.id === gid ? { ...g, label: newLabel } : g)),
    );

  const renameGroup = (oldGid, newNameRaw) => {
    const newName = (newNameRaw || '').trim();
    if (!newName) return;
    // prevent collision
    if (groups.some((g) => g.id !== oldGid && g.id === newName)) return;
    setGroups((gs) =>
      gs.map((g) =>
        g.id === oldGid ? { ...g, id: newName, name: newName } : g
      ),
    );
    // if currently selected targetGroup equals old id, move it too
    setTargetGroup((tg) => (tg === oldGid ? newName : tg));
  };

  const moveSelected = () => {
    if (!targetGroup || !selected.size) return;
    const arr = [...selected];
    setUngrouped((u) => u.filter((h) => !selected.has(h)));
    setGroups((gs) =>
      gs.map((g) => (g.id === targetGroup ? { ...g, columns: [...g.columns, ...arr] } : g))
    );
    setActive((a) => {
      const n = new Set(a);
      arr.forEach((h) => n.delete(h));
      return n;
    });
    setSelected(new Set());
  };

  const openLabelEditor = (hdr) => {
    setLabelDlgHdr(hdr);
    setLabelDlgVarLabel(getEffectiveVarLabel(hdr));
    const vl = getEffectiveValueLabels(hdr);
    const text = Object.entries(vl).map(([k, v]) => `${k}=${v}`).join('\n');
    setLabelDlgValueLabels(text);
    setLabelDlgOpen(true);
  };

  const parseValueLabelsText = (text) => {
    const obj = {};
    if (!text) return obj;
    text.split('\n').forEach((line) => {
      const raw = line.trim();
      if (!raw) return;
      // support "code=label" or "code: label"
      const parts = raw.includes('=') ? raw.split('=') : raw.split(':');
      if (parts.length >= 2) {
        const code = String(parts[0]).trim();
        const label = parts.slice(1).join(raw.includes('=') ? '=' : ':').trim();
        if (code) obj[code] = label;
      }
    });
    return obj;
  };

  const saveLabelEditor = () => {
    const newMap = new Map(labelEdits);
    newMap.set(labelDlgHdr, {
      varLabel: labelDlgVarLabel || '',
      valueLabels: parseValueLabelsText(labelDlgValueLabels),
    });
    setLabelEdits(newMap);
    setLabelDlgOpen(false);
  };

  /* -------- save -------- */
  const persistStateToDB = useCallback(
    async ({ closeDialog = true, overrideHeaderLikert, overrideGroupLikert } = {}) => {
      if (!project) return false;
      setSaving(true);
      try {
        const map = {};
        (project.rawData || []).forEach((c) => (map[c.header] = { ...c }));
        // Clear ALL existing other links and re-activate any previously linked headers
        const previouslyLinked = new Set();
        (project.rawData || []).forEach((col) => {
          if (col.otherTextHeader) previouslyLinked.add(col.otherTextHeader);
          if (col.otherCodeHeader) previouslyLinked.add(col.otherCodeHeader);
        });
        Object.values(map).forEach((c) => {
          if ('otherTextHeader' in c) delete c.otherTextHeader;
          if ('otherCodeHeader' in c) delete c.otherCodeHeader;
        });
        previouslyLinked.forEach((h) => {
          if (map[h]) map[h].active = true;
        });
        // Apply ONLY current Other links and deactivate newly linked other columns
        const linkedSet = new Set();
        // Upsert sys_othersDataset so Other dialog can load without Reset
        const othersArray = [];
        otherLinks.forEach((v, base) => {
          const codes = Array.isArray(v.codes) ? v.codes : (v.code ? [v.code] : []);
          othersArray.push({ base, text: v.text || '', codes });
        });
        const metaKey = 'sys_othersDataset';
        const existingMeta = map[metaKey];
        const metaPayload = {
          header: metaKey,
          type: 'meta',
          active: false,
          varLabel: JSON.stringify(othersArray),
          othersDataset: othersArray,
        };
        map[metaKey] = existingMeta ? { ...existingMeta, ...metaPayload } : metaPayload;

        otherLinks.forEach((v, base) => {
          const codes = Array.isArray(v.codes) ? v.codes : (v.code ? [v.code] : []);
          if (map[base]) {
            map[base].otherTextHeader = v.text || '';
            map[base].otherCodeHeader = codes[0] || '';
            map[base].otherCodeHeaders = codes;
          }
          if (v.text && map[v.text]) {
            linkedSet.add(v.text);
            map[v.text].active = false;
          }
          codes.forEach((c) => {
            if (c && map[c]) {
              linkedSet.add(c);
              map[c].active = false;
            }
          });
        });
        // ungrouped
        ungrouped.forEach((h) => {
          if (linkedSet.has(h)) return; // skip hidden other columns
          const c = map[h];
          c.active = active.has(h);
          delete c.groupname;
          delete c.grouptype;
          delete c.m;
          delete c.r;
          delete c.c;
          c.manualUngroup = true;
        });
        // grouped
        groups.forEach((g) =>
          g.columns.forEach((h) => {
            if (linkedSet.has(h)) return; // skip hidden other columns
            const c = map[h];
            c.groupname = g.name;
            c.grouptype = g.grouptype;
            c.groupVisible = typeof g.visible === 'boolean' ? g.visible : true;
            if (typeof g.label === 'string' && g.label.trim() !== '') {
              c.groupLabel = g.label.trim();
            } else {
              delete c.groupLabel; // undefined yerine property'yi tamamen kaldır
            }
            delete c.manualUngroup;
            c.active = active.has(h);
          })
        );
        // merge label edits into map
        for (const [hdr, payload] of labelEdits.entries()) {
          const c = map[hdr];
          if (!c) continue;
          const vlabel = typeof payload.varLabel === 'string' ? payload.varLabel.trim() : '';
          if (vlabel) {
            c.varLabel = vlabel;
          } else {
            delete c.varLabel; // undefined yazma
          }
          const vls = payload.valueLabels && typeof payload.valueLabels === 'object' ? payload.valueLabels : {};
          c.valueLabels = vls;
        }

        const headerLikertMap = overrideHeaderLikert || likertByHeader;
        for (const [hdr, payload] of headerLikertMap.entries()) {
          const c = map[hdr];
          if (!c) continue;
          if (payload?.isLikert) {
            c.isLikert = true;
            c.likertScale = Array.isArray(payload.scale) ? payload.scale : [];
          } else {
            delete c.isLikert;
            delete c.likertScale;
          }
        }

        const groupLikertMap = overrideGroupLikert || likertByGroup;
        for (const [gid, payload] of groupLikertMap.entries()) {
          const g = groups.find((x) => x.id === gid);
          if (!g) continue;
          for (const hdr of g.columns || []) {
            const c = map[hdr];
            if (!c) continue;
            if (payload?.isLikert) {
              c.groupLikert = true;
              c.groupLikertScale = Array.isArray(payload.scale) ? payload.scale : [];
            } else {
              delete c.groupLikert;
              delete c.groupLikertScale;
            }
          }
        }

        const cloneColumnMeta = (hdr) => {
          if (!hdr) return null;
          const col = map[hdr];
          if (!col || typeof col !== 'object') return null;
          try {
            return JSON.parse(JSON.stringify(col));
          } catch (err) {
            console.warn('main_questions serialization error for header:', hdr, err);
            return null;
          }
        };

        const serializedGroups = groups.map((g, groupIndex) => {
          const columns = (Array.isArray(g.columns) ? g.columns : [])
            .map((hdr, columnIndex) => {
              if (!hdr || linkedSet.has(hdr)) return null;
              if (/^sys_/i.test(hdr)) return null;
              const snapshot = cloneColumnMeta(hdr);
              if (!snapshot) return null;
              return {
                order: columnIndex,
                ...snapshot,
              };
            })
            .filter(Boolean);

          const likertData = (() => {
            const data = groupLikertMap.get(g.id);
            if (!data) {
              return { isLikert: false, scale: [] };
            }
            return {
              isLikert: !!data.isLikert,
              scale: Array.isArray(data.scale) ? data.scale : [],
            };
          })();

          return {
            id: g.id,
            name: g.name,
            order: groupIndex,
            grouptype: g.grouptype || 'multiplechoice',
            visible: typeof g.visible === 'boolean' ? g.visible : true,
            label: typeof g.label === 'string' ? g.label : '',
            likert: likertData,
            columns,
          };
        });

        const serializedUngrouped = ungrouped
          .filter((hdr) => hdr && !linkedSet.has(hdr) && !/^sys_/i.test(hdr))
          .map((hdr, index) => {
            const snapshot = cloneColumnMeta(hdr);
            if (!snapshot) return null;
            return {
              order: index,
              ...snapshot,
            };
          })
          .filter(Boolean);

        const serializedOtherLinks = Array.from(otherLinks.entries()).map(([base, v]) => {
          const codes = Array.isArray(v?.codes)
            ? v.codes
            : v?.code
            ? [v.code]
            : [];
          return {
            base,
            text: v?.text || '',
            codes,
          };
        });

        // Build unified questions array with sub-schema: { id, label, details }
        const groupsAsQuestions = serializedGroups.map((g) => {
          const { id, label, ...rest } = g;
          return {
            id,
            label: typeof label === 'string' ? label : '',
            details: rest, // everything except id & label goes under details
          };
        });

        const ungroupedAsQuestions = serializedUngrouped.map((u) => {
          // Use header as id, varLabel as label; keep the remainder under details
          const { header, varLabel, ...rest } = u;
          return {
            id: header,
            label: typeof varLabel === 'string' ? varLabel : '',
            details: rest,
          };
        });

        const unifiedQuestions = [...groupsAsQuestions, ...ungroupedAsQuestions];

        const mainQuestionsPayload = {
          generatedAt: new Date().toISOString(),
          questions: unifiedQuestions,
          otherLinks: serializedOtherLinks,
        };

        const mainQuestionsKey = 'sys_mainQuestions';
        const existingMainMeta = map[mainQuestionsKey];
        const mainQuestionsMetaPayload = {
          header: mainQuestionsKey,
          type: 'meta',
          active: false,
          main_questions: mainQuestionsPayload,
        };

        console.log('mainQuestionsPayload', mainQuestionsPayload)
        map[mainQuestionsKey] = existingMainMeta
          ? { ...existingMainMeta, ...mainQuestionsMetaPayload }
          : mainQuestionsMetaPayload;

        const result = onSave ? onSave(project.projectID, Object.values(map)) : undefined;
        if (result && typeof result.then === 'function') {
          await result;
        }
        if (closeDialog) {
          onClose();
        }
        return true;
      } catch (err) {
        console.error('ColumnSelectorDialog save error:', err);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      project,
      onSave,
      onClose,
      otherLinks,
      ungrouped,
      groups,
      active,
      labelEdits,
      likertByHeader,
      likertByGroup,
    ]
  );

  useEffect(() => {
    if (!pendingPersistAfterResetRef.current) return;
    if (!project?.projectID) return;
    pendingPersistAfterResetRef.current = false;
    (async () => {
      await persistStateToDB({ closeDialog: false });
    })();
  }, [
    project,
    groups,
    ungrouped,
    active,
    labelEdits,
    likertByHeader,
    likertByGroup,
    otherLinks,
    persistStateToDB,
  ]);

  const handleSave = useCallback(async () => {
    await persistStateToDB({ closeDialog: true });
  }, [persistStateToDB]);

  const commitLikertDialog = useCallback(async () => {
    const { kind, id } = likertDlgTarget || {};
    if (!kind || !id) {
      setLikertDlgOpen(false);
      return;
    }
    const normalizedScale = Array.isArray(likertDlgScale)
      ? likertDlgScale.map((row) => ({
          code: row?.code != null ? String(row.code) : '',
          label: row?.label != null ? String(row.label) : '',
          value: Number.isFinite(row?.value) ? row.value : 0,
        }))
      : [];

    if (kind === 'header') {
      const nextMap = new Map(likertByHeader);
      nextMap.set(id, { isLikert: !!likertDlgIsLikert, scale: normalizedScale });
      setLikertByHeader(nextMap);
      const ok = await persistStateToDB({ closeDialog: false, overrideHeaderLikert: nextMap });
      if (ok) setLikertDlgOpen(false);
    } else {
      const nextMap = new Map(likertByGroup);
      nextMap.set(id, { isLikert: !!likertDlgIsLikert, scale: normalizedScale });
      setLikertByGroup(nextMap);
      const ok = await persistStateToDB({ closeDialog: false, overrideGroupLikert: nextMap });
      if (ok) setLikertDlgOpen(false);
    }
  }, [
    likertDlgTarget,
    likertDlgIsLikert,
    likertDlgScale,
    likertByHeader,
    likertByGroup,
    persistStateToDB,
  ]);

  /* -------- UI -------- */
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle disableTypography sx={{ p: 0 }}>
        <Box
          sx={{
            position: 'relative',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
            background: 'linear-gradient(135deg, rgba(191, 219, 254, 0.95) 0%, rgba(244, 219, 255, 0.9) 100%)',
            color: '#0f172a',
            borderBottom: '1px solid rgba(148, 163, 184, 0.25)',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', top: 12, right: 12, color: '#0f172a' }}
          >
            <CloseIcon />
          </IconButton>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('selectGraphsTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(15,23,42,0.7)' }}>
                Sütun gruplarınızı düzenleyin, etiketleri güncelleyin ve Likert ayarlarını yönetin.
              </Typography>
              {heroChips.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  {heroChips.map((chip) => (
                    <Chip key={chip} label={chip} size="small" sx={chipSx} />
                  ))}
                </Stack>
              )}
            </Box>
            <Stack
              spacing={1}
              alignItems={{ xs: 'stretch', lg: 'flex-end' }}
              sx={{ width: { xs: '100%', lg: 'auto' } }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                useFlexGap
                alignItems="center"
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ bgcolor: 'rgba(255,255,255,0.55)', px: 1.5, py: 0.7, borderRadius: 2 }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    SPSS etiketleri
                  </Typography>
                  <Switch size="small" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} />
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ ...primaryButtonSx, background: '#ffffff' }}
                >
                  Reset
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setOtherDlgOpen(true)}
                  sx={{ ...primaryButtonSx, background: '#ffffff' }}
                >
                  Other Eşle
                </Button>
              </Stack>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                useFlexGap
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <TextField
                  size="small"
                  label={t('newGroup')}
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  sx={{ minWidth: { md: 220 } }}
                />
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addGroup}
                  disabled={!newGroupName.trim()}
                  sx={{
                    ...primaryButtonSx,
                    background: 'linear-gradient(135deg, #bfdbfe 0%, #dbeafe 100%)',
                    color: '#0f172a',
                  }}
                >
                  Grup Oluştur
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, backgroundColor: '#f8fafc' }}>
        <Box
          sx={{
            px: { xs: 2.5, md: 3 },
            py: { xs: 2, md: 3 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{ ...surfaceBaseSx, display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            <TextField
              size="small"
              placeholder={t('search')}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'rgba(15,23,42,0.5)' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Çoklu seçim: Shift basılı tutarak aralıktaki sütunları seçin. Sürükle-bırak ile gruplara taşıyın, kalem ikonundan etiketleri düzenleyin.
            </Typography>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              useFlexGap
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <TextField
                size="small"
                select
                label={t('moveTo')}
                value={targetGroup}
                onChange={(e) => setTargetGroup(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ minWidth: 200 }}
              >
                <option value="" disabled>
                  {t('selectGroup')}
                </option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </TextField>
              <Button
                variant="outlined"
                disabled={!selected.size || !targetGroup}
                onClick={moveSelected}
                sx={{ ...primaryButtonSx, background: '#ffffff' }}
              >
                {t('moveSelected')}
              </Button>
              <Typography variant="caption" sx={{ ml: { xs: 0, md: 'auto' } }}>
                {selected.size} {t('selected')}
              </Typography>
            </Stack>
          </Paper>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Stack spacing={2}>
              <Droppable droppableId="ungrouped">
                {(provided) => (
                  <Paper
                    elevation={0}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      ...surfaceBaseSx,
                      borderRadius: 3,
                      border: '1px dashed rgba(148, 163, 184, 0.45)',
                      backgroundColor: '#ffffff',
                      maxHeight: 340,
                      overflow: 'auto',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('ungroupedColumns')}
                    </Typography>
                    {filteredUngrouped.map((h, i) => (
                      <Draggable key={h} draggableId={h} index={i}>
                        {(prov) => (
                          <MuiTooltip title={getValueLabelPreview(h) || ''} placement="top" arrow>
                            <Box
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              onClick={(e) => toggleSelectByIdx(i, h, e)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 1,
                                py: 0.75,
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                bgcolor: selected.has(h) ? 'rgba(99,102,241,0.12)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(148,163,184,0.15)' },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    flex: 1,
                                    pr: 1,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {getDisplayName(h)}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openLikertEditorForHeader(h);
                                  }}
                                  aria-label="Skala düzenle"
                                >
                                  <ScaleIcon fontSize="inherit" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openLabelEditor(h);
                                  }}
                                  aria-label="Etiketleri düzenle"
                                >
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              </Box>
                              <Checkbox
                                size="small"
                                checked={active.has(h)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleActive(h)}
                              />
                            </Box>
                          </MuiTooltip>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Paper>
                )}
              </Droppable>

              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                }}
              >
                {groups.map((g) => (
                  <Droppable droppableId={`group-${g.id}`} key={g.id}>
                    {(prov) => (
                      <Paper
                        elevation={0}
                        sx={{
                          ...surfaceBaseSx,
                          borderRadius: 3,
                          border: '1px solid rgba(148, 163, 184, 0.3)',
                          backgroundColor: '#ffffff',
                          maxHeight: 360,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                          <TextField
                            size="small"
                            variant="outlined"
                            label="Grup Adı"
                            value={g.name}
                            onChange={(e) => renameGroup(g.id, e.target.value)}
                            sx={{ minWidth: 150, flex: 1 }}
                          />
                          <TextField
                            size="small"
                            variant="outlined"
                            label="Grup Etiketi"
                            value={g.label || ''}
                            onChange={(e) => setGroupLabel(g.id, e.target.value)}
                            sx={{ minWidth: 150, flex: 1 }}
                          />
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption">Göster</Typography>
                            <Switch
                              size="small"
                              checked={g.visible !== false}
                              onChange={(e) => setGroupVisibility(g.id, e.target.checked)}
                            />
                          </Stack>
                          <TextField
                            size="small"
                            select
                            value={g.grouptype}
                            onChange={(e) => setGroupType(g.id, e.target.value)}
                            SelectProps={{ native: true }}
                            sx={{ minWidth: 140 }}
                          >
                            {GROUP_TYPES.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </TextField>
                          <IconButton size="small" onClick={() => openLikertEditorForGroup(g.id)} aria-label="Grup skala düzenle">
                            <ScaleIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteGroup(g)}>
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </Stack>

                        <Box
                          ref={prov.innerRef}
                          {...prov.droppableProps}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            overflow: 'auto',
                          }}
                        >
                          {g.columns
                            .filter((h) => !isHiddenByOther(h))
                            .map((h, idx) => (
                              <Draggable key={h} draggableId={h} index={idx}>
                                {(drag) => (
                                  <MuiTooltip title={getValueLabelPreview(h) || ''} placement="top" arrow>
                                    <Box
                                      ref={drag.innerRef}
                                      {...drag.draggableProps}
                                      {...drag.dragHandleProps}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: 2,
                                        border: '1px solid rgba(148,163,184,0.2)',
                                        '&:hover': { borderColor: 'rgba(99,102,241,0.4)' },
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 14,
                                          flex: 1,
                                          minWidth: 0,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {getDisplayName(h)}
                                      </Typography>
                                      <Checkbox
                                        size="small"
                                        checked={active.has(h)}
                                        onChange={() => toggleActive(h)}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={() => openLikertEditorForHeader(h)}
                                        aria-label="Skala düzenle"
                                      >
                                        <ScaleIcon fontSize="inherit" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => openLabelEditor(h)}
                                        aria-label="Etiketleri düzenle"
                                      >
                                        <EditIcon fontSize="inherit" />
                                      </IconButton>
                                    </Box>
                                  </MuiTooltip>
                                )}
                              </Draggable>
                            ))}
                          {prov.placeholder}
                        </Box>
                      </Paper>
                    )}
                  </Droppable>
                ))}
              </Box>
            </Stack>
          </DragDropContext>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2.5, md: 3 },
          py: { xs: 2, md: 2.5 },
          backgroundColor: '#f8fafc',
          borderTop: '1px solid rgba(148, 163, 184, 0.2)',
          gap: 1,
        }}
      >
        <Button onClick={onClose} sx={{ ...primaryButtonSx, background: '#ffffff' }}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            ...primaryButtonSx,
            background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
            color: '#0f172a',
          }}
        >
          {t('save')}
        </Button>
      </DialogActions>

      {/* Other linking dialog */}
      <Dialog open={otherDlgOpen} onClose={() => setOtherDlgOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Other Alanlarını Birleştir</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField select label="Ana Soru (Base)" size="small" value={newOtherBase} onChange={(e) => setNewOtherBase(e.target.value)} sx={{ minWidth: 240 }}>
              {(ALL_HEADERS.filter(isPureBase).length ? ALL_HEADERS.filter(isPureBase) : ALL_HEADERS).map(h => (
                <MenuItem key={h} value={h}>{getDisplayName(h)}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Other (Metin)" size="small" value={newOtherText} onChange={(e) => setNewOtherText(e.target.value)} sx={{ minWidth: 260 }}>
              {(ALL_HEADERS.filter(isOtherText).length ? ALL_HEADERS.filter(isOtherText) : ALL_HEADERS.filter(h => /_other/i.test(h))).map(h => (
                <MenuItem key={h} value={h}>{getDisplayName(h)}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Other (Kod)" size="small" value={newOtherCode} onChange={(e) => setNewOtherCode(e.target.value)} sx={{ minWidth: 260 }}>
              {(ALL_HEADERS.filter(isOtherCode).length ? ALL_HEADERS.filter(isOtherCode) : ALL_HEADERS.filter(h => /_other_?(?:code|kod)/i.test(h))).map(h => (
                <MenuItem key={h} value={h}>{getDisplayName(h)}</MenuItem>
              ))}
            </TextField>
            <Button variant="contained" onClick={() => {
              if (!newOtherBase || !newOtherText || !newOtherCode) return;
              setOtherLinks(prev => {
                const m = new Map(prev);
                m.set(newOtherBase, { text: newOtherText, code: newOtherCode });
                const cur = m.get(newOtherBase);
                if (cur) {
                  const codes = Array.isArray(cur.codes) ? cur.codes.slice() : (cur.code ? [cur.code] : []);
                  if (!codes.includes(newOtherCode)) codes.push(newOtherCode);
                  m.set(newOtherBase, { text: newOtherText, codes });
                } else {
                  m.set(newOtherBase, { text: newOtherText, codes: [newOtherCode] });
                }
                return m;
              });
              // clear and hide selected text/code from lists automatically
              setNewOtherBase(''); setNewOtherText(''); setNewOtherCode('');
            }}>Ekle</Button>
          </Box>

          {/* Existing links */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption">Base</Typography>
            <Typography variant="caption">Other (Metin)</Typography>
            <Typography variant="caption">Other (Kod)</Typography>
            <Typography variant="caption"></Typography>
            {[...otherLinks.entries()].map(([base, v]) => (
              <React.Fragment key={base}>
                <Typography sx={{ fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getDisplayName(base)}</Typography>
                <Typography sx={{ fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getDisplayName(v.text)}</Typography>
                <Typography sx={{ fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {(() => {
                    const codes = Array.isArray(v.codes) ? v.codes : (v.code ? [v.code] : []);
                    return codes.map(getDisplayName).join(', ');
                  })()}
                </Typography>
                <Button size="small" onClick={() => setOtherLinks(prev => { const m = new Map(prev); m.delete(base); return m; })}>Kaldır</Button>
              </React.Fragment>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={async () => {
            setOtherSaving(true);
            try {
              await persistOtherLinksSilently(otherLinks);
              setOtherDlgOpen(false); // close sub-dialog
              if (typeof onClose === 'function') onClose(); // also close main selectGraphsTitle dialog
            } finally {
              setOtherSaving(false);
            }
          }} disabled={otherSaving}>
            {t('save')}
          </Button>
          <Button onClick={() => setOtherDlgOpen(false)} disabled={otherSaving}>{t('close') || 'Kapat'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={labelDlgOpen}
        onClose={() => setLabelDlgOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Etiketleri Düzenle — {labelDlgHdr}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Soru Etiketi (VarLabel)"
              value={labelDlgVarLabel}
              onChange={(e) => setLabelDlgVarLabel(e.target.value)}
              fullWidth
            />
            <TextField
              label="Değer Etiketleri"
              value={labelDlgValueLabels}
              onChange={(e) => setLabelDlgValueLabels(e.target.value)}
              multiline
              minRows={6}
              helperText={`Her satıra "kod=etiket" veya "kod: etiket" yazın. Örn: 1=Evet`}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelDlgOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={saveLabelEditor}>{t('save')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={likertDlgOpen} onClose={() => setLikertDlgOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {likertDlgTarget.kind === 'group' ? 'Grup Skala Ayarları' : 'Skala Ayarları'} — {likertDlgTarget.id}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:2 }}>
            <Typography>Likert skala</Typography>
            <Switch size="small" checked={likertDlgIsLikert} onChange={(e)=>setLikertDlgIsLikert(e.target.checked)} />
          </Box>
          {likertDlgIsLikert && (
            <Box sx={{ display:'grid', gridTemplateColumns:'1fr 3fr 1fr', gap:1, alignItems:'center' }}>
              <Typography variant="caption">Kod</Typography>
              <Typography variant="caption">Etiket</Typography>
              <Typography variant="caption">Değer</Typography>
              {likertDlgScale.map((row, idx) => (
                <React.Fragment key={idx}>
                  <TextField size="small" value={row.code}
                    onChange={(e)=>setLikertDlgScale(p=>{ const a=[...p]; a[idx]={...a[idx], code:e.target.value}; return a; })} />
                  <TextField size="small" value={row.label}
                    onChange={(e)=>setLikertDlgScale(p=>{ const a=[...p]; a[idx]={...a[idx], label:e.target.value}; return a; })} />
                  <TextField size="small" type="number" value={row.value}
                    onChange={(e)=>{ const v = parseFloat(e.target.value||'0'); setLikertDlgScale(p=>{ const a=[...p]; a[idx]={...a[idx], value:v}; return a; }); }} />
                </React.Fragment>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLikertDlgOpen(false)}>{t('cancel')}</Button>
          <Button onClick={commitLikertDialog} disabled={saving}>{t('save')}</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
});

export default ColumnSelectorDialog;
